import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Globe, ChevronUp, ChevronDown, MoreHorizontal, PlusCircle, Eye, Power, Share2, Trash2 } from "lucide-react";
import { Link } from "@tanstack/react-router";
import { toast } from "sonner";
import { useLingui } from "@lingui/react/macro";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";
import { regionApi } from "@/lib/api/admin-api";
import { ConfirmDeleteDialog } from "@/components/common/ConfirmDeleteDialog";
import { AddLocationsDialog } from "./AddLocationsDialog";
import type { Region } from "@/types/region";

// ─── Location sub-item ────────────────────────────────────────────────────────

function LocationItem({
  location,
}: {
  location: NonNullable<Region["locations"]>[number];
}) {
  const { t } = useLingui();

  return (
    <div className="bg-(--color-surface-muted) border border-(--color-border) rounded-lg p-3 flex items-center gap-3">
      <div className="flex flex-1 items-center gap-3 min-w-0">
        <div className="size-13 rounded-[10px] bg-(--color-surface-accent) shrink-0 overflow-hidden border border-(--shell-border) flex items-center justify-center">
          <Globe className="size-5 text-(--color-text-secondary)" />
        </div>
        <div className="flex flex-col gap-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-base font-medium text-(--color-text-primary) whitespace-nowrap">
              {location.name}
            </span>
            <span
              className={cn(
                "px-2 py-0.5 rounded-full text-sm font-medium whitespace-nowrap",
                location.isActive
                  ? "bg-(--color-status-active) text-(--brand-white)"
                  : "bg-(--color-surface-accent) text-(--color-text-secondary)",
              )}
            >
              {location.isActive ? t`Active` : t`Inactive`}
            </span>
          </div>
          <span className="text-sm text-(--color-text-secondary) overflow-hidden text-ellipsis whitespace-nowrap">
            {[location.code, location.city, location.timezone]
              .filter(Boolean)
              .join(" • ")}
          </span>
        </div>
      </div>
      <button
        type="button"
        className="size-13 bg-(--color-surface-content) border border-(--shell-border) rounded-[10px] flex items-center justify-center shrink-0 hover:bg-(--color-surface-muted) transition-colors"
      >
        <MoreHorizontal className="size-5 text-(--color-text-secondary)" />
      </button>
    </div>
  );
}

// ─── Region card ──────────────────────────────────────────────────────────────

export function RegionCard({
  region,
  expanded,
  onToggle,
}: {
  region: Region;
  expanded: boolean;
  onToggle: () => void;
}) {
  const { t } = useLingui();
  const queryClient = useQueryClient();
  const [removeOpen, setRemoveOpen] = useState(false);
  const [addLocationsOpen, setAddLocationsOpen] = useState(false);

  const locationCount =
    region._count?.locations ?? region.locations?.length ?? 0;

  const toggleActiveMutation = useMutation({
    mutationFn: () => regionApi.update(region.id, { isActive: !region.isActive }),
    onSuccess: (updated) => {
      void queryClient.invalidateQueries({ queryKey: ["regions"] });
      toast.success(updated.isActive ? t`Region set as active` : t`Region set as inactive`);
    },
    onError: () => toast.error(t`Failed to update region status`),
  });

  const deleteMutation = useMutation({
    mutationFn: () => regionApi.delete(region.id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["regions"] });
      toast.success(t`Region removed`);
    },
    onError: () => toast.error(t`Failed to remove region`),
  });

  const handleShare = () => {
    const url = `${window.location.origin}/regions/${region.id}`;
    void navigator.clipboard.writeText(url).then(() => {
      toast.success(t`Link copied to clipboard`);
    });
  };

  return (
    <>
      <div
        className={cn(
          "border border-(--color-border) rounded-[10px] overflow-hidden",
          expanded ? "bg-(--color-surface-content)" : "bg-(--color-surface-muted)",
        )}
      >
        {/* Header row */}
        <div
          className={cn(
            "flex items-center gap-3 p-3",
            expanded && "border-b border-(--color-border) bg-(--color-surface-muted)",
          )}
        >
          <button
            type="button"
            onClick={onToggle}
            className="size-12 bg-(--color-surface-content) border border-(--color-border) rounded-[10px] flex items-center justify-center shrink-0 hover:bg-(--color-surface-muted) transition-colors"
          >
            {expanded ? (
              <ChevronUp className="size-5 text-(--color-text-secondary)" />
            ) : (
              <ChevronDown className="size-5 text-(--color-text-secondary)" />
            )}
          </button>

          <div className="flex-1 min-w-0 flex flex-col gap-1">
            <Link
              to="/regions/$regionId"
              params={{ regionId: String(region.id) }}
              className="text-base font-medium text-(--color-text-primary) whitespace-nowrap hover:underline w-fit"
            >
              {region.name}
            </Link>
            <div className="flex items-center gap-2 text-base leading-snug">
              <span className="text-(--color-text-secondary)">{t`Locations:`}</span>
              <span className="font-medium text-(--color-text-primary)">{locationCount}</span>
            </div>
          </div>

          <div className="flex items-center gap-3 shrink-0">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  className="size-12 bg-(--color-surface-content) border border-(--color-border) rounded-[10px] flex items-center justify-center hover:bg-(--color-surface-muted) transition-colors"
                >
                  <MoreHorizontal className="size-5 text-(--color-text-secondary)" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-52">
                <DropdownMenuItem asChild>
                  <Link
                    to="/regions/$regionId"
                    params={{ regionId: String(region.id) }}
                    className="flex items-center gap-2"
                  >
                    <Eye className="size-4" />
                    {t`View`}
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem
                  className="gap-2 cursor-pointer"
                  disabled={toggleActiveMutation.isPending}
                  onSelect={() => toggleActiveMutation.mutate()}
                >
                  <Power className="size-4" />
                  {region.isActive ? t`Set as inactive` : t`Set as active`}
                </DropdownMenuItem>
                <DropdownMenuItem
                  className="gap-2 cursor-pointer"
                  onSelect={() => setAddLocationsOpen(true)}
                >
                  <PlusCircle className="size-4" />
                  {t`Add location`}
                </DropdownMenuItem>
                <DropdownMenuItem className="gap-2 cursor-pointer" onSelect={handleShare}>
                  <Share2 className="size-4" />
                  {t`Share`}
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  className="gap-2 cursor-pointer text-(--color-danger-text) focus:text-(--color-danger-text)"
                  onSelect={() => setRemoveOpen(true)}
                >
                  <Trash2 className="size-4" />
                  {t`Remove`}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            <Button
              className="h-12 gap-2 rounded-[10px] bg-(--color-action) hover:bg-(--color-action)/90 text-(--color-action-fg) px-4"
              onClick={() => setAddLocationsOpen(true)}
            >
              <PlusCircle className="size-5" />
              {t`Add location`}
            </Button>
          </div>
        </div>

        {/* Expanded: location sub-items */}
        {expanded && (
          <div className="p-4 flex flex-col gap-2">
            {region.locations && region.locations.length > 0 ? (
              region.locations.map((loc) => (
                <LocationItem key={loc.id} location={loc} />
              ))
            ) : (
              <p className="py-4 text-center text-sm text-(--color-text-secondary)">
                {t`No locations in this region yet.`}
              </p>
            )}
          </div>
        )}
      </div>

      <AddLocationsDialog
        open={addLocationsOpen}
        onOpenChange={setAddLocationsOpen}
        regionId={region.id}
        regionName={region.name}
      />

      <ConfirmDeleteDialog
        open={removeOpen}
        onOpenChange={setRemoveOpen}
        title={t`Remove region?`}
        description={t`This will permanently remove ${region.name} from your organization.`}
        bullets={[t`This action cannot be undone`]}
        confirmLabel={t`Remove region`}
        onConfirm={() => deleteMutation.mutate()}
        isPending={deleteMutation.isPending}
      />
    </>
  );
}
